import React, { useMemo, useState } from "react";
import styled from "styled-components";
import SourceStatusBadge from "./components/SourceStatusBadge";
import SourceDetailDrawer from "./components/SourceDetailDrawer";
import KPICard from "./components/KPICard";
import { SourceStatsRow, TimeSeriesPoint } from "../../api/ParsersPanel";

const DROP_COLOR_MIN = { r: 0xff, g: 0xe0, b: 0xb2 }; // light orange (small drop)
const DROP_COLOR_MAX = { r: 0xe6, g: 0x51, b: 0x00 }; // #e65100 (100% drop)

const getDropColor = (pct: number): string => {
  const ratio = Math.min(Math.max(pct, 0), 100) / 100;
  const r = Math.round(DROP_COLOR_MIN.r + (DROP_COLOR_MAX.r - DROP_COLOR_MIN.r) * ratio);
  const g = Math.round(DROP_COLOR_MIN.g + (DROP_COLOR_MAX.g - DROP_COLOR_MIN.g) * ratio);
  const b = Math.round(DROP_COLOR_MIN.b + (DROP_COLOR_MAX.b - DROP_COLOR_MIN.b) * ratio);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
};

type SortKey = keyof Pick<
  SourceStatsRow,
  "source_link" | "count_last_week" | "count_prev_week" | "drop_pct" | "count_3mo" | "last_collection_at"
>;

interface SourcesTabProps {
  rows: SourceStatsRow[];
  loading: boolean;
  error: string | null;
  node: "tamrur" | "arabic";
  /** Column header text for the source identifier (e.g. "Source URL", "Source"). */
  sourceColumnLabel: string;
  /** Returns the daily time series for a given source link. */
  fetchTimeseries: (
    token: string,
    sourceLink: string,
    days: number,
  ) => Promise<TimeSeriesPoint[]>;
}

/**
 * Generic dashboard tab that renders per-source health metrics for any
 * snapshot-backed source (Tamrur, Arabic, …).  Caller supplies the data
 * and the timeseries fetcher; everything else (KPI cards, table, mobile
 * cards, search, sort, detail drawer) is handled here.
 */
const SourcesTab: React.FC<SourcesTabProps> = ({
  rows,
  loading,
  error,
  node,
  sourceColumnLabel,
  fetchTimeseries,
}) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("drop_pct");
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<SourceStatsRow | null>(null);

  const totalSilent = rows.filter((r) => r.status === "silent").length;
  const totalDropping = rows.filter((r) => r.status === "dropping").length;
  const totalOk = rows.filter((r) => r.status === "ok").length;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => !q || (r.source_link ?? "").toLowerCase().includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? (sortKey === "drop_pct" ? -Infinity : "");
      const bv = b[sortKey] ?? (sortKey === "drop_pct" ? -Infinity : "");
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(false); }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " ▲" : " ▼") : "";

  return (
    <Container>
      <TopRow>
        <KPICards>
          <KPICard label="Total Sources" value={rows.length} />
          <KPICard label="Healthy" value={totalOk} accentHealthy />
          <KPICard label="Dropping" value={totalDropping} accent />
          <KPICard label="Silent" value={totalSilent} accentSilent/>
        </KPICards>

        <SearchInput
          type="text"
          placeholder={`Search ${sourceColumnLabel.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </TopRow>

      {loading && <Msg>Loading…</Msg>}
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {!loading && !error && (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort("source_link")}>
                    {sourceColumnLabel} {sortIndicator("source_link")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("last_collection_at")}>
                    Last Collected {sortIndicator("last_collection_at")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("count_last_week")}>
                    Last 7d {sortIndicator("count_last_week")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("count_prev_week")}>
                    Prev 7d {sortIndicator("count_prev_week")}
                  </Th>
                  <Th align="right" title="% below (or above, if negative) the median of the trailing 6 weeks, not a simple week-over-week comparison." onClick={() => toggleSort("drop_pct")}>
                    Difference % {sortIndicator("drop_pct")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("count_3mo")}>
                    3 Months {sortIndicator("count_3mo")}
                  </Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <Td colSpan={7}>
                      <Msg>No sources found.</Msg>
                    </Td>
                  </tr>
                ) : (
                  sorted.map((row, i) => (
                    <Tr key={row.source_link ?? i} onClick={() => setSelected(row)}>
                      <Td>
                        <SourceUrl>{row.source_link ?? "-"}</SourceUrl>
                      </Td>
                      <Td align="right">{row.last_collection_at ?? "-"}</Td>
                      <Td align="right">{row.count_last_week.toLocaleString()}</Td>
                      <Td align="right">{row.count_prev_week.toLocaleString()}</Td>
                      <Td align="right">
                        {row.drop_pct !== null ? (
                          <DropPct $dropping={row.drop_pct > 0} $color={getDropColor(row.drop_pct)}>
                            {row.drop_pct > 0 ? "▼" : "▲"} {Math.abs(row.drop_pct)}%
                          </DropPct>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td align="right">{row.count_3mo.toLocaleString()}</Td>
                      <Td>
                        <SourceStatusBadge status={row.status} />
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrapper>

          <CardList>
            {sorted.length === 0 ? (
              <Msg>No sources found.</Msg>
            ) : (
              sorted.map((row, i) => (
                <Card key={row.source_link ?? i} onClick={() => setSelected(row)}>
                  <CardHeader>
                    <SourceUrl>{row.source_link ?? "-"}</SourceUrl>
                    <SourceStatusBadge status={row.status} />
                  </CardHeader>
                  <CardGrid>
                    <CardCell>
                      <CellLabel>Last Collected</CellLabel>
                      <CellValue>{row.last_collection_at ?? "-"}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>Last 7d</CellLabel>
                      <CellValue>{row.count_last_week.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>Prev 7d</CellLabel>
                      <CellValue>{row.count_prev_week.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>3 Months</CellLabel>
                      <CellValue>{row.count_3mo.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel title="% below (or above, if negative) the median of the trailing 6 weeks, not a simple week-over-week comparison.">Difference %</CellLabel>
                      <CellValue>
                        {row.drop_pct !== null ? (
                          <DropPct $dropping={row.drop_pct > 0} $color={getDropColor(row.drop_pct)}>
                            {row.drop_pct > 0 ? "▼" : "▲"} {Math.abs(row.drop_pct)}%
                          </DropPct>
                        ) : (
                          "-"
                        )}
                      </CellValue>
                    </CardCell>
                  </CardGrid>
                </Card>
              ))
            )}
          </CardList>
        </>
      )}

      {selected && (
        <SourceDetailDrawer
          source={selected}
          node={node}
          onClose={() => setSelected(null)}
          fetchTimeseries={fetchTimeseries}
        />
      )}
    </Container>
  );
};

export default SourcesTab;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const KPICards = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  min-width: 260px;
  &:focus { outline: none; border-color: #0058A9; }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  min-width: 750px;
`;

const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  padding: 14px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f6f9ff; }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
`;

const CardCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const CellLabel = styled.span`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const CellValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
`;

const Th = styled.th<{ align?: string }>`
  background: #0058A9;
  color: #fff;
  text-align: ${({ align }) => align ?? "left"};
  padding: 10px 14px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  &:hover { background: #004a90; }
`;

const Tr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  &:hover { background: #f6f9ff; }
`;

const Td = styled.td<{ align?: string }>`
  padding: 9px 14px;
  text-align: ${({ align }) => align ?? "left"};
  vertical-align: middle;
`;

const SourceUrl = styled.span`
  font-size: 12px;
  color: #0058A9;
  word-break: break-all;
`;

const DropPct = styled.span<{ $dropping: boolean; $color?: string }>`
  font-weight: 700;
  color: ${({ $dropping, $color }) => ($dropping ? $color ?? "#e65100" : "#2e7d32")};
`;

const Msg = styled.p`
  color: #aaa;
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorMsg = styled.p`
  color: #c62828;
  font-size: 14px;
`;
